import type { RouteConfig } from "@/routes/routes.types";
import type { AppDispatch, RootState } from "@/store/store";
import classNames from "classnames";
import { dequal } from "dequal";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import Tooltip from "@/components/common/Tooltip/Tooltip";
import SignoutIcon from "@assets/icons/common/signout.svg?react";
import userService from "@/endpoints/userService";
import { useState, type Dispatch, type SetStateAction, useMemo } from "react";
import { setPendingSelectionIfChanged } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import ArrowIcon from "@assets/icons/common/angle_down.svg?react";
import ICONS from "@/constant/IconManager";
import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";
import { MARKETS, POINT_IN_TIME } from "@/constant/constantsThrougoutProject";
import InfoPopover from "@/components/common/InfoPopover/InfoPopover";
import { getPageDocumentation } from "@/constants/pageDocumentation";

interface SideNavSubRoute {
  activeRoute: RouteConfig | null;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
}

const SideNavSubRoute = ({ activeRoute, setIsExpanded }: SideNavSubRoute) => {
  const { pathname } = useLocation();
  const [isTabAccordionOpen, setIsTabAccordionOpen] = useState(true);
  const [isSavedViewAccordionOpen, setIsSavedViewAccordionOpen] =
    useState(true);
  const [isInfoPopoverOpen, setIsInfoPopoverOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(
    (state: RootState) => state.userDetail || null,
    (prev, next) => {
      return dequal(prev, next);
    },
  );

  const attributeLabels = useSelector(
    (state: RootState) => {
      if (
        ROUTES_CHILDREN_LOOKUP[pathname]?.multipleOptionsForKeys?.[
          POINT_IN_TIME
        ]?.includes(MARKETS)
      ) {
        return state.activeGroup.attributeLabels["global"] || {};
      } else {
        if (state.userSelection.markets) {
          const currentMarket = state.userSelection.markets[0];
          if (state.activeGroup.attributeLabels[currentMarket]) {
            return state.activeGroup.attributeLabels[currentMarket];
          }
          return state.activeGroup.attributeLabels["default"] || {};
        }
      }
      return state.activeGroup.attributeLabels["default"] || {};
    },
    (prev, next) => dequal(prev, next),
  );

  const handleGetUrlParams = (pathName: string) => {
    const parts = pathName.split("/").filter(Boolean);
    const module = parts[0] || "";
    const screenType = parts[1] || "brand";
    dispatch(setPendingSelectionIfChanged({ module, screenType } as any));
  };

  const title =
    attributeLabels?.[`${activeRoute?.key}`]?.label ?? activeRoute?.name;

  const documentationContent = useMemo(() => {
    return getPageDocumentation(activeRoute?.key);
  }, [activeRoute?.key]);

  const handleInfoIconClick = () => {
    if (documentationContent) {
      setIsInfoPopoverOpen(true);
    }
  };

  return (
    <div className="flex flex-col justify-between h-full">
      {activeRoute ? (
        <>
          <div className="">
            <div className="flex items-center justify-start gap-0 pr-2 pb-4 pt-[10px] pl-2 mx-4 border-b-[2px] border-[#1b404d54] pt-[18px] text-base">
              <div className="flex gap-[10px] font-normal tracking-[0.5px] text-large items-center flex-1 min-w-0">
                {activeRoute.iconKey ? (
                  <img
                    src={ICONS[activeRoute.iconKey]?.light}
                    alt={activeRoute.name}
                    className="h-[18px] w-[18px] flex-shrink-0"
                  />
                ) : (
                  ""
                )}
                <div className="truncate text-medium">{title}</div>
              </div>
              {documentationContent ? (
                <Tooltip
                  contentList={["View documentation"]}
                  offset={[0, 5]}
                  placement="right"
                >
                  <button
                    type="button"
                    onClick={handleInfoIconClick}
                    className="flex items-center justify-center w-[28px] h-[28px] rounded-[6px] border border-[#1b404d50] bg-[#1b404d20] hover:bg-[#1b404d35] hover:border-[#1b404d80] hover:shadow-[0_2px_8px_rgba(27,64,77,0.15)] active:bg-[#1b404d40] active:scale-[0.96] active:shadow-[0_1px_4px_rgba(27,64,77,0.12)] transition-all duration-200 ease-out cursor-pointer flex-shrink-0 ml-[6px] group focus:outline-none focus:ring-2 focus:ring-[#1b404d50] focus:ring-offset-2 focus:ring-offset-[#1b404d]"
                    aria-label={`View documentation for ${title}`}
                  >
                    <img
                      className="h-[16px] w-[16px] opacity-85 group-hover:opacity-100 transition-opacity duration-200 ease-out"
                      src={ICONS.INFORMATION.light}
                      alt="Information"
                    />
                  </button>
                </Tooltip>
              ) : (
                <Tooltip
                  contentList={[`This is ${title?.toLowerCase()} tab`]}
                  offset={[0, 5]}
                  placement="right"
                >
                  <div className="flex items-center justify-center w-[28px] h-[28px] rounded-[6px] border border-[#1b404d40] bg-[#1b404d15] opacity-60 flex-shrink-0 ml-[6px]">
                    <img
                      className="h-[16px] w-[16px]"
                      src={ICONS.INFORMATION.light}
                      alt="Information"
                    />
                  </div>
                </Tooltip>
              )}
            </div>
            <div className="mt-[22px]">
              <div
                className="text-[#5B5A64] px-3 pl-[23px] text-sm flex items-center gap-[2px] cursor-pointer"
                onClick={() => setIsTabAccordionOpen(!isTabAccordionOpen)}
              >
                <ArrowIcon className="h-[16px] font-light text-xs" />
                <div>Core</div>
              </div>
              <ul
                className={classNames(
                  "mb-[25px] mr-[16px] mt-[7px] ml-[16px] text-medium font-very-light transition-all duration-300 overflow-hidden",
                  isTabAccordionOpen ? "max-h-40" : "max-h-0 ",
                )}
              >
                {activeRoute.children?.map((route) => {
                  return (
                    <li
                      key={route.id}
                      className={classNames(
                        "mb-[8px] pt-[9.5px] pb-[9.5px] pl-[12px] pr-[12px] rounded-[4px] cursor-pointer pl-3",
                        pathname?.startsWith(`/${route.id}`)
                          ? "bg-ternary-500"
                          : "",
                      )}
                      onClick={() => {
                        handleGetUrlParams(route.redirectTo);
                        setIsExpanded(false);
                      }}
                    >
                      {route.name}
                    </li>
                  );
                })}
              </ul>
            </div>
            {/* <div className="mt-[22px]">
              <div
                className="text-[#5B5A64] px-3 pl-[23px] text-sm flex items-center gap-[2px] cursor-pointer"
                onClick={() =>
                  setIsSavedViewAccordionOpen(!isSavedViewAccordionOpen)
                }
              >
                <ArrowIcon className="h-[16px] font-light text-xs" />
                <div>Saved Views</div>
              </div>
              <ul
                className={classNames(
                  "m-3 mr-[16px] mt-[9px] ml-[16px] font-very-light text-medium transition-all duration-300 overflow-hidden",
                  isSavedViewAccordionOpen ? "max-h-fit" : "max-h-0 "
                )}
              >
                {[
                  "Saved view 1",
                  "Saved view 2",
                  "Saved view 3",
                  "Saved view 4",
                ]?.map((route) => {
                  return (
                    <li
                      key={route}
                      className={classNames(
                        "mb-3 p-2 rounded-[4px] cursor-pointer pl-3"
                      )}
                      onClick={() => {}}
                    >
                      {route}
                    </li>
                  );
                })}
              </ul>
            </div> */}
          </div>
        </>
      ) : (
        <div></div>
      )}
      <div className="w-full">
        <div className="border-t-[2px] pr-0 py-[19px] pl-0 border-[#1b404d54] mx-4 text-base flex items-center justify-between">
          <div className="w-[calc(100%-22px)] h-[40px]">
            {user?.name ? <div className="font-medium">{user?.name}</div> : ""}
            {user?.email && (
              <Tooltip
                contentList={[
                  user?.email ? user?.email : "Something went wrong",
                ]}
                offset={[0, 5]}
                placement="top"
              >
                <div className="text-xs font-light text-ellipsis overflow-hidden opacity-70 w-full">
                  {user?.email}
                </div>
              </Tooltip>
            )}
          </div>
          <Tooltip contentList={["Sign out"]} offset={[0, 5]} placement="right">
            <div
              className="p-1 cursor-pointer"
              onClick={userService.auth.logout}
            >
              <SignoutIcon height={22} />
            </div>
          </Tooltip>
        </div>
      </div>
      {documentationContent && (
        <InfoPopover
          isOpen={isInfoPopoverOpen}
          onClose={() => setIsInfoPopoverOpen(false)}
          title={title}
          content={documentationContent}
          iconKey={activeRoute?.iconKey}
        />
      )}
    </div>
  );
};

export default SideNavSubRoute;
